import { useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import { Language } from '../../../../../../common/types/i18n.js'
import { formatPhoneDisplay } from '../../../../../../common/utils/format-utils.js'
import { formatIban, normalizeIban } from '../../../../../../common/utils/iban.js'
import { Button } from '../../../../../components/button/button.js'
import { CopyButton } from '../../../../../components/copy-button/copy-button.js'
import { Checkbox } from '../../../../../components/forms/checkbox/checkbox.js'
import { Select } from '../../../../../components/forms/select/select.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { User } from '../../../../../components/icon/solar-icons.js'
import { FieldRow, Section, SectionHeader } from '../../../../../components/section/section.js'
import { getWorkModeLabel } from '../../../utils/format.js'
import { cityLabel, countryLabel } from '../../../utils/locale-labels.js'
import { PHONE_PLACEHOLDER, phoneFieldNote } from '../../../utils/phone-field.js'
import type {
    EmployeeProfileData,
    EmploymentRecordInput,
    EmploymentRefData,
    LocationChangeData,
} from '../employee-profile-types.js'
import '../employee-profile-view.css'

interface GeneralInfoSectionProps {
    employee: EmployeeProfileData
    lang: Language
    onUpdatePersonal?: (data: Record<string, string>) => Promise<boolean>
    // Reference lists + callback for the dated employment fields (job title,
    // team, manager, location, work mode) inside the merged edit form.
    employmentRefData?: EmploymentRefData
    onCreateEmploymentRecord?: (data: EmploymentRecordInput) => Promise<boolean>
    // Manager-confirmed work-location change flow (self requests, manager decides).
    locationChange?: LocationChangeData
}

// Local YYYY-MM-DD for the default "effective from" date.
const today = (): string => new Date().toISOString().slice(0, 10)

interface EmployeeOption {
    id: number
    first_name: string
    last_name: string
}

// Build the "Otsene juht" picker options from the active-employee list:
// drop the employee themselves (can't be your own manager), always keep the
// currently-set manager even if they've since been offboarded and dropped from
// the active list (so the saved value still renders), then sort by display name.
// The leading "—" clear option is prepended at the call site.
export const buildManagerOptions = (
    employees: EmployeeOption[],
    { selfId, current }: { selfId: number; current: EmployeeOption | null },
): { value: string; label: string }[] => {
    const rows = employees.filter((e) => e.id !== selfId)
    if (current && current.id !== selfId && !rows.some((e) => e.id === current.id)) {
        rows.push(current)
    }
    return rows
        .map((e) => ({ value: String(e.id), label: `${e.first_name} ${e.last_name}`.trim() }))
        .sort((a, b) => a.label.localeCompare(b.label))
}

const WORK_MODES = ['office', 'hybrid', 'remote', 'abroad'] as const

// City value is the stored English name; the display label comes from `cityLabel`.
interface CityOption {
    value: string
    tz: string
}

// Country value is the ISO code; the display label comes from `countryLabel`
// (Intl.DisplayNames in the viewer's language).
const COUNTRY_OPTIONS: { value: string; cities: CityOption[] }[] = [
    {
        value: 'EE',
        cities: [
            { value: 'Tallinn', tz: 'Europe/Tallinn' },
            { value: 'Tartu', tz: 'Europe/Tallinn' },
        ],
    },
    {
        value: 'FI',
        cities: [
            { value: 'Helsinki', tz: 'Europe/Helsinki' },
            { value: 'Tampere', tz: 'Europe/Helsinki' },
        ],
    },
    {
        value: 'LV',
        cities: [{ value: 'Riga', tz: 'Europe/Riga' }],
    },
    {
        value: 'LT',
        cities: [{ value: 'Vilnius', tz: 'Europe/Vilnius' }],
    },
    {
        value: 'SE',
        cities: [{ value: 'Stockholm', tz: 'Europe/Stockholm' }],
    },
    {
        value: 'NO',
        cities: [{ value: 'Oslo', tz: 'Europe/Oslo' }],
    },
    {
        value: 'DK',
        cities: [{ value: 'Copenhagen', tz: 'Europe/Copenhagen' }],
    },
    {
        value: 'DE',
        cities: [
            { value: 'Berlin', tz: 'Europe/Berlin' },
            { value: 'Munich', tz: 'Europe/Berlin' },
        ],
    },
    {
        value: 'GB',
        cities: [
            { value: 'London', tz: 'Europe/London' },
            { value: 'Edinburgh', tz: 'Europe/London' },
        ],
    },
    {
        value: 'US',
        cities: [
            { value: 'New York', tz: 'America/New_York' },
            { value: 'Los Angeles', tz: 'America/Los_Angeles' },
            { value: 'Chicago', tz: 'America/Chicago' },
            { value: 'Denver', tz: 'America/Denver' },
            { value: 'Anchorage', tz: 'America/Anchorage' },
            { value: 'Honolulu', tz: 'Pacific/Honolulu' },
        ],
    },
    {
        value: 'CA',
        cities: [
            { value: 'Toronto', tz: 'America/Toronto' },
            { value: 'Vancouver', tz: 'America/Vancouver' },
            { value: 'Calgary', tz: 'America/Edmonton' },
            { value: 'Winnipeg', tz: 'America/Winnipeg' },
            { value: 'Halifax', tz: 'America/Halifax' },
        ],
    },
    {
        value: 'FR',
        cities: [{ value: 'Paris', tz: 'Europe/Paris' }],
    },
    {
        value: 'ES',
        cities: [
            { value: 'Madrid', tz: 'Europe/Madrid' },
            { value: 'Barcelona', tz: 'Europe/Madrid' },
        ],
    },
    {
        value: 'IT',
        cities: [
            { value: 'Rome', tz: 'Europe/Rome' },
            { value: 'Milan', tz: 'Europe/Rome' },
        ],
    },
    {
        value: 'NL',
        cities: [{ value: 'Amsterdam', tz: 'Europe/Amsterdam' }],
    },
    {
        value: 'PL',
        cities: [{ value: 'Warsaw', tz: 'Europe/Warsaw' }],
    },
    {
        value: 'PT',
        cities: [
            { value: 'Lisbon', tz: 'Europe/Lisbon' },
            { value: 'Porto', tz: 'Europe/Lisbon' },
        ],
    },
    {
        value: 'CZ',
        cities: [{ value: 'Prague', tz: 'Europe/Prague' }],
    },
    {
        value: 'AT',
        cities: [{ value: 'Vienna', tz: 'Europe/Vienna' }],
    },
    {
        value: 'CH',
        cities: [{ value: 'Zurich', tz: 'Europe/Zurich' }],
    },
    {
        value: 'BE',
        cities: [{ value: 'Brussels', tz: 'Europe/Brussels' }],
    },
    {
        value: 'IE',
        cities: [{ value: 'Dublin', tz: 'Europe/Dublin' }],
    },
    {
        value: 'UA',
        cities: [{ value: 'Kyiv', tz: 'Europe/Kyiv' }],
    },
    {
        value: 'AU',
        cities: [
            { value: 'Sydney', tz: 'Australia/Sydney' },
            { value: 'Melbourne', tz: 'Australia/Melbourne' },
            { value: 'Perth', tz: 'Australia/Perth' },
            { value: 'Brisbane', tz: 'Australia/Brisbane' },
            { value: 'Adelaide', tz: 'Australia/Adelaide' },
        ],
    },
    {
        value: 'JP',
        cities: [{ value: 'Tokyo', tz: 'Asia/Tokyo' }],
    },
]

interface GeneralInfoFormState {
    effective_from: string
    job_title: string
    location_id: string
    manager_id: string
    work_mode: string
}

/**
 * "Üldinfo" — everyday work contact + organisational belonging (TASK-438).
 * One "Muuda" button opens a single form whose fields follow the viewer's
 * rights: each in-place field group (name, work email, role details,
 * phone/languages) gates on its own field-level edit capability resolved
 * server-side (TASK-476), and the location change goes through the
 * manager-confirmed request flow; employment editors also get the dated fields
 * (job title, team, manager, location, work mode) which create a new SCD2
 * history record on change.
 */
export const GeneralInfoSection = ({
    employee,
    lang,
    onUpdatePersonal,
    employmentRefData,
    onCreateEmploymentRecord,
    locationChange,
}: GeneralInfoSectionProps) => {
    const ph = t.portal
    const emp = employee.employment
    const viewer = employee.viewer

    // Plain in-place field groups — each follows the role editor's field-level
    // edit grants (self-baseline included), not a hardcoded admin/self flag.
    const canEditName = (viewer?.canEditName ?? false) && !!onUpdatePersonal
    const canEditWorkEmail = (viewer?.canEditWorkEmail ?? false) && !!onUpdatePersonal
    const canEditRoleDetails = (viewer?.canEditRoleDetails ?? false) && !!onUpdatePersonal
    const canEditContact = (viewer?.canEditContact ?? false) && !!onUpdatePersonal
    const canEditInfo = canEditName || canEditWorkEmail || canEditRoleDetails || canEditContact
    // Dated employment fields — changing them creates a new SCD2 record.
    const canEditEmployment =
        (viewer?.canEditEmployment ?? false) && !!onCreateEmploymentRecord && !!employmentRefData

    const [isEditing, setIsEditing] = useState(false)
    const [draft, setDraft] = useState<Record<string, string>>({})
    const [employmentForm, setEmploymentForm] = useState<GeneralInfoFormState>({
        effective_from: '',
        job_title: '',
        location_id: '',
        manager_id: '',
        work_mode: '',
    })
    const [teamIds, setTeamIds] = useState<number[]>([])
    const [saving, setSaving] = useState(false)

    const openEdit = () => {
        setDraft({
            _location_id: typeof emp?.location_id === 'number' ? String(emp.location_id) : '',
            // Each group goes into the draft only with its edit grant — the
            // server rejects the whole PATCH if any submitted field lacks
            // edit permission, so a locked field must never be submitted.
            ...(canEditContact
                ? {
                      spoken_languages: employee.spoken_languages ?? '',
                      work_phone: emp?.work_phone ?? '',
                  }
                : {}),
            ...(canEditName
                ? {
                      first_name: employee.first_name ?? '',
                      last_name: employee.last_name ?? '',
                  }
                : {}),
            ...(canEditWorkEmail ? { work_email: emp?.work_email ?? '' } : {}),
            ...(canEditRoleDetails ? { role_details: emp?.role_details ?? '' } : {}),
        })
        setEmploymentForm({
            effective_from: today(),
            job_title: emp?.job_title ?? '',
            location_id: typeof emp?.location_id === 'number' ? String(emp.location_id) : '',
            manager_id: typeof emp?.manager_id === 'number' ? String(emp.manager_id) : '',
            work_mode: emp?.work_mode ?? '',
        })
        setTeamIds(emp?.teams.map((tm) => tm.id) ?? [])
        setIsEditing(true)
    }

    const currentTeamIds = emp?.teams.map((tm) => tm.id) ?? []
    const teamsChanged =
        teamIds.length !== currentTeamIds.length ||
        teamIds.some((id) => !currentTeamIds.includes(id))
    // Only a real change to a dated field creates a history record — touching
    // just the phone/languages/identity fields stays an in-place update.
    const employmentChanged =
        canEditEmployment &&
        (employmentForm.job_title !== (emp?.job_title ?? '') ||
            teamsChanged ||
            employmentForm.manager_id !==
                (typeof emp?.manager_id === 'number' ? String(emp.manager_id) : '') ||
            employmentForm.location_id !==
                (typeof emp?.location_id === 'number' ? String(emp.location_id) : '') ||
            employmentForm.work_mode !== (emp?.work_mode ?? ''))

    const save = async () => {
        setSaving(true)
        try {
            const { _location_id, _custom_country, _custom_city, ...personalData } = draft

            // Self location change goes through the manager-confirmed request
            // flow; employment editors set it directly on the new record below.
            const isCustom = _location_id === '_custom'
            const locationRequested =
                !canEditEmployment &&
                locationChange?.canRequest &&
                (isCustom
                    ? _custom_country && _custom_city?.trim()
                    : _location_id && Number(_location_id) !== emp?.location_id)
            const customTz = isCustom
                ? COUNTRY_OPTIONS.find((c) => c.value === _custom_country)?.cities.find(
                      (c) => c.value === _custom_city,
                  )?.tz
                : undefined

            // In-place fields first, so the dated record created below carries
            // the freshly saved values forward.
            const okPersonal =
                canEditInfo && onUpdatePersonal ? await onUpdatePersonal(personalData) : true
            const okLocation = locationRequested
                ? isCustom
                    ? await locationChange.onRequestCustom(
                          _custom_city.trim(),
                          _custom_country,
                          customTz,
                      )
                    : await locationChange.onRequest(Number(_location_id))
                : true

            let okRecord = true
            // Only record the dated change once the in-place saves succeeded —
            // otherwise a rejected PATCH value would be copied onto the new
            // history record and the two would disagree.
            if (okPersonal && okLocation && employmentChanged && onCreateEmploymentRecord) {
                // Carry forward the fields this form doesn't put on the record
                // so the new active record stays complete (createRecord nulls
                // anything omitted).
                okRecord = await onCreateEmploymentRecord({
                    effective_from: employmentForm.effective_from,
                    job_title: employmentForm.job_title || null,
                    team_ids: teamIds,
                    manager_id: employmentForm.manager_id
                        ? Number(employmentForm.manager_id)
                        : null,
                    location_id: employmentForm.location_id
                        ? Number(employmentForm.location_id)
                        : null,
                    work_mode: employmentForm.work_mode || null,
                    collaboration_type: emp?.collaboration_type ?? null,
                    work_email: (canEditWorkEmail ? draft.work_email : emp?.work_email) || null,
                    work_phone: (canEditContact ? draft.work_phone : emp?.work_phone) || null,
                    role_details:
                        (canEditRoleDetails ? draft.role_details : emp?.role_details) || null,
                    hire_date: emp?.hire_date ?? null,
                    contract_start: emp?.contract_start ?? null,
                })
            }
            if (okPersonal && okLocation && okRecord) setIsEditing(false)
        } finally {
            setSaving(false)
        }
    }

    // Work-location change request — manager approve/reject state
    const [locationBusy, setLocationBusy] = useState(false)

    const decideLocationRequest = (action: 'approve' | 'reject') => {
        if (!locationChange?.pending) return
        setLocationBusy(true)
        void locationChange.onDecide(locationChange.pending.id, action).then(() => {
            setLocationBusy(false)
        })
    }

    const selfLocationEditor = !canEditEmployment && locationChange?.canRequest

    return (
        <Section>
            <SectionHeader
                icon={User}
                title={ph.profile.generalInfoSection(lang)}
                actions={
                    (canEditInfo || canEditEmployment) &&
                    !isEditing && (
                        <Button
                            variant="secondary"
                            size="small"
                            text={ph.profile.editProfile(lang)}
                            onClick={openEdit}
                        />
                    )
                }
            />

            {isEditing ? (
                <>
                    {!canEditEmployment && (
                        <p className="portal-profile__edit-note">{ph.profile.managedByHr(lang)}</p>
                    )}
                    {canEditName && (
                        <>
                            <FieldRow label={ph.profile.firstName(lang)}>
                                <TextField
                                    value={draft.first_name ?? ''}
                                    onChange={(value) =>
                                        setDraft((prev) => ({ ...prev, first_name: value }))
                                    }
                                />
                            </FieldRow>
                            <FieldRow label={ph.profile.lastName(lang)}>
                                <TextField
                                    value={draft.last_name ?? ''}
                                    onChange={(value) =>
                                        setDraft((prev) => ({ ...prev, last_name: value }))
                                    }
                                />
                            </FieldRow>
                        </>
                    )}
                    {canEditWorkEmail && (
                        <FieldRow label={ph.profile.workEmail(lang)}>
                            <TextField
                                inputProps={{ type: 'email', maxLength: 255 }}
                                value={draft.work_email ?? ''}
                                onChange={(value) =>
                                    setDraft((prev) => ({ ...prev, work_email: value }))
                                }
                            />
                        </FieldRow>
                    )}

                    {canEditEmployment ? (
                        <>
                            <FieldRow label={ph.profile.jobTitle(lang)}>
                                <TextField
                                    value={employmentForm.job_title}
                                    onChange={(value) =>
                                        setEmploymentForm((prev) => ({
                                            ...prev,
                                            job_title: value,
                                        }))
                                    }
                                />
                            </FieldRow>
                            <FieldRow label={ph.profile.team(lang)}>
                                <div className="portal-profile__checkbox-group">
                                    {(employmentRefData?.teams ?? []).map((tm) => (
                                        <Checkbox
                                            key={tm.id}
                                            size="small"
                                            label={tm.name}
                                            isChecked={teamIds.includes(tm.id)}
                                            onChange={(checked) =>
                                                setTeamIds((prev) =>
                                                    checked
                                                        ? [...prev, tm.id]
                                                        : prev.filter((id) => id !== tm.id),
                                                )
                                            }
                                        />
                                    ))}
                                </div>
                            </FieldRow>
                            <FieldRow label={ph.profile.manager(lang)}>
                                <Select
                                    placeholder="—"
                                    value={employmentForm.manager_id}
                                    onChange={(value) =>
                                        setEmploymentForm((prev) => ({
                                            ...prev,
                                            manager_id: value,
                                        }))
                                    }
                                    options={[
                                        // Employment editors may clear anyone's
                                        // manager (top-of-org profiles, leavers).
                                        { value: '', label: '—' },
                                        ...buildManagerOptions(employmentRefData?.employees ?? [], {
                                            selfId: employee.id,
                                            current:
                                                typeof emp?.manager_id === 'number'
                                                    ? {
                                                          id: emp.manager_id,
                                                          first_name: emp.manager_first_name ?? '',
                                                          last_name: emp.manager_last_name ?? '',
                                                      }
                                                    : null,
                                        }),
                                    ]}
                                />
                            </FieldRow>
                            <FieldRow label={ph.profile.location(lang)}>
                                <Select
                                    placeholder="—"
                                    value={employmentForm.location_id}
                                    onChange={(value) =>
                                        setEmploymentForm((prev) => ({
                                            ...prev,
                                            location_id: value,
                                        }))
                                    }
                                    options={(employmentRefData?.locations ?? []).map((loc) => ({
                                        value: String(loc.id),
                                        label: loc.name,
                                    }))}
                                />
                            </FieldRow>
                            <FieldRow label={ph.profile.workMode(lang)}>
                                <Select
                                    placeholder="—"
                                    value={employmentForm.work_mode}
                                    onChange={(value) =>
                                        setEmploymentForm((prev) => ({
                                            ...prev,
                                            work_mode: value,
                                        }))
                                    }
                                    options={WORK_MODES.map((mode) => ({
                                        value: mode,
                                        label: ph.profile.workModes[mode](lang),
                                    }))}
                                />
                            </FieldRow>
                            {employmentChanged && (
                                <>
                                    <FieldRow label={ph.profile.effectiveFrom(lang)}>
                                        <TextField
                                            inputProps={{ type: 'date' }}
                                            value={employmentForm.effective_from}
                                            onChange={(value) =>
                                                setEmploymentForm((prev) => ({
                                                    ...prev,
                                                    effective_from: value,
                                                }))
                                            }
                                        />
                                    </FieldRow>
                                    <p className="portal-profile__edit-note">
                                        {ph.profile.employmentChangeHint(lang)}
                                    </p>
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <FieldRow label={ph.profile.team(lang)}>
                                {emp?.teams && emp.teams.length > 0
                                    ? emp.teams.map((team) => team.name).join(', ')
                                    : '—'}
                            </FieldRow>
                            <FieldRow label={ph.profile.manager(lang)}>
                                {emp?.manager_first_name
                                    ? `${emp.manager_first_name} ${emp.manager_last_name}`
                                    : '—'}
                            </FieldRow>
                            <FieldRow label={ph.profile.location(lang)}>
                                {selfLocationEditor ? (
                                    <>
                                        <Select
                                            placeholder="—"
                                            value={draft._location_id ?? ''}
                                            onChange={(value) =>
                                                setDraft((prev) => ({
                                                    ...prev,
                                                    _location_id: value,
                                                    ...(value !== '_custom'
                                                        ? {
                                                              _custom_country: '',
                                                              _custom_city: '',
                                                          }
                                                        : {}),
                                                }))
                                            }
                                            options={[
                                                ...(locationChange?.locations ?? []).map((loc) => ({
                                                    value: String(loc.id),
                                                    label: loc.name,
                                                })),
                                                {
                                                    value: '_custom',
                                                    label: ph.profile.locationOther(lang),
                                                },
                                            ]}
                                        />
                                        {draft._location_id === '_custom' && (
                                            <div className="portal-profile__custom-location">
                                                <Select
                                                    placeholder={ph.profile.locationCountry(lang)}
                                                    value={draft._custom_country ?? ''}
                                                    onChange={(value) =>
                                                        setDraft((prev) => ({
                                                            ...prev,
                                                            _custom_country: value,
                                                            _custom_city: '',
                                                        }))
                                                    }
                                                    options={COUNTRY_OPTIONS.map((c) => ({
                                                        value: c.value,
                                                        label: countryLabel(c.value, lang),
                                                    }))}
                                                />
                                                <Select
                                                    placeholder={ph.profile.locationCity(lang)}
                                                    value={draft._custom_city ?? ''}
                                                    onChange={(value) =>
                                                        setDraft((prev) => ({
                                                            ...prev,
                                                            _custom_city: value,
                                                        }))
                                                    }
                                                    options={
                                                        COUNTRY_OPTIONS.find(
                                                            (c) =>
                                                                c.value === draft._custom_country,
                                                        )?.cities.map((c) => ({
                                                            value: c.value,
                                                            label: cityLabel(c.value, lang),
                                                        })) ?? []
                                                    }
                                                />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    (emp?.location_name ?? '—')
                                )}
                            </FieldRow>
                            <FieldRow label={ph.profile.workMode(lang)}>
                                {getWorkModeLabel(emp?.work_mode, lang)}
                            </FieldRow>
                        </>
                    )}

                    {canEditContact && (
                        <>
                            <FieldRow label={ph.profile.workPhone(lang)}>
                                <TextField
                                    inputProps={{ type: 'tel', maxLength: 32 }}
                                    placeholder={PHONE_PLACEHOLDER}
                                    value={draft.work_phone ?? ''}
                                    onChange={(value) =>
                                        setDraft((prev) => ({ ...prev, work_phone: value }))
                                    }
                                    note={phoneFieldNote(draft.work_phone ?? '', lang)}
                                />
                            </FieldRow>
                            <FieldRow label={ph.profile.spokenLanguages(lang)}>
                                <TextField
                                    value={draft.spoken_languages ?? ''}
                                    onChange={(value) =>
                                        setDraft((prev) => ({
                                            ...prev,
                                            spoken_languages: value,
                                        }))
                                    }
                                />
                            </FieldRow>
                        </>
                    )}
                    {canEditRoleDetails && (
                        <FieldRow label={ph.profile.roleDetails(lang)}>
                            <TextField
                                as="textarea"
                                value={draft.role_details ?? ''}
                                textAreaProps={{ maxLength: 500, rows: 3 }}
                                onChange={(value) =>
                                    setDraft((prev) => ({ ...prev, role_details: value }))
                                }
                            />
                        </FieldRow>
                    )}

                    <div className="portal-profile__edit-actions">
                        <Button
                            text={ph.profile.saveChanges(lang)}
                            size="small"
                            isDisabled={
                                saving || (employmentChanged && !employmentForm.effective_from)
                            }
                            onClick={() => void save()}
                        />
                        <Button
                            text={t.common.cancel(lang)}
                            variant="secondary"
                            size="small"
                            onClick={() => setIsEditing(false)}
                        />
                    </div>
                </>
            ) : (
                <>
                    <FieldRow label={ph.profile.jobTitle(lang)}>{emp?.job_title ?? '—'}</FieldRow>
                    <FieldRow label={ph.profile.team(lang)}>
                        {emp?.teams && emp.teams.length > 0
                            ? emp.teams.map((team) => team.name).join(', ')
                            : '—'}
                    </FieldRow>
                    <FieldRow label={ph.profile.manager(lang)}>
                        {emp?.manager_first_name
                            ? `${emp.manager_first_name} ${emp.manager_last_name}`
                            : '—'}
                    </FieldRow>
                    <FieldRow label={ph.profile.location(lang)}>
                        <div className="portal-profile__location-cell">
                            <span>{emp?.location_name ?? '—'}</span>
                            {locationChange?.pending && (
                                <span className="portal-profile__pending-italic">
                                    {ph.profile.locationPending(
                                        locationChange.pending.newLabel ?? '?',
                                    )(lang)}
                                </span>
                            )}
                            {locationChange?.pending && locationChange.canDecide && (
                                <span className="portal-profile__location-actions">
                                    <Button
                                        variant="primary"
                                        size="small"
                                        text={ph.profile.locationApprove(lang)}
                                        isDisabled={locationBusy}
                                        onClick={() => decideLocationRequest('approve')}
                                    />
                                    <Button
                                        variant="secondary"
                                        size="small"
                                        text={ph.profile.locationReject(lang)}
                                        isDisabled={locationBusy}
                                        onClick={() => decideLocationRequest('reject')}
                                    />
                                </span>
                            )}
                        </div>
                    </FieldRow>
                    <FieldRow label={ph.profile.workMode(lang)}>
                        {getWorkModeLabel(emp?.work_mode, lang)}
                    </FieldRow>
                    <FieldRow label={ph.profile.workPhone(lang)}>
                        {emp?.work_phone ? (
                            <a href={`tel:${emp.work_phone}`} className="portal-profile__link">
                                {formatPhoneDisplay(emp.work_phone)}
                            </a>
                        ) : (
                            '—'
                        )}
                    </FieldRow>
                    {/* Public since 22.10.2026 so colleagues can pay each other back; the bank form
                        (private tier) is where it is edited. */}
                    {employee.iban && (
                        <FieldRow label={ph.profile.iban(lang)} monospace>
                            {formatIban(employee.iban)}
                            <CopyButton
                                value={normalizeIban(employee.iban)}
                                label={ph.profile.copyIban(lang)}
                                copiedLabel={t.common.copied(lang)}
                            />
                        </FieldRow>
                    )}
                    {(canEditContact || employee.spoken_languages) && (
                        <FieldRow label={ph.profile.spokenLanguages(lang)}>
                            {employee.spoken_languages ?? '—'}
                        </FieldRow>
                    )}
                    {emp?.role_details && (
                        <FieldRow label={ph.profile.roleDetails(lang)}>{emp.role_details}</FieldRow>
                    )}
                </>
            )}
        </Section>
    )
}
